/* eslint-disable @typescript-eslint/no-unused-vars */

import { PrometheusMetrics } from '@/tracking/prometheus/prometheus.interfaces';
import { pushStatsig } from '@/tracking/statsig';
import {
    MetricsGateway,
    NumericEvent,
    OtherEvent,
    ErrorEvent,
} from '@/tracking/metrics/metrics.interfaces';
import {
    mapStatsigError,
    mapStatsigNumeric,
    mapStatsigOther,
} from '@/tracking/metrics/metrics.helpers';
import { ErrorCategory, ErrorSeverity, ErrorSource } from '@/api/Errors/errors.interfaces';

export class PrometheusMetricsGateway implements MetricsGateway {
    constructor(
        private prometheus: PrometheusMetrics,
        private defaultBuckets: number[] = [0.1, 0.5, 1, 2, 5, 10]
    ) {}

    other(event: OtherEvent): void {
        const counter = this.prometheus.getCounter(event.type);
        counter.inc({});
    }

    numeric(event: NumericEvent): void {
        if (event.type.includes('duration')) {
            const histogram = this.prometheus.getHistogram(event.type, this.defaultBuckets);

            histogram.observe({}, event.value);
        } else {
            const counter = this.prometheus.getCounter(event.type);

            counter.inc({}, event.value);
        }
    }

    error(event: ErrorEvent): void {
        const context = event.context || {};

        const counter = this.prometheus.getCounter(event.type);
        counter.inc({
            status: 'error',
            page: context.page || 'unknown',
            error_source: context.errorSource || ErrorSource.Application,
            severity: context.severity || ErrorSeverity.P1,
            error_category: context.errorCategory || ErrorCategory.Client,
            market: context.market || 'unknown',
            locale: context.locale || 'unknown',
            component: context.component || 'unknown',
            error_message: event.error?.message || 'unknown',
        });
    }
}

export class StatsigMetricsGateway implements MetricsGateway {
    other(event: OtherEvent): void {
        const mapped = mapStatsigOther(event);
        pushStatsig(mapped);
    }
    numeric(event: NumericEvent): void {
        const mapped = mapStatsigNumeric(event);
        pushStatsig(mapped);
    }
    error(event: ErrorEvent): void {
        const mapped = mapStatsigError(event);
        pushStatsig(mapped);
    }
}

export class ConsoleMetricsGateway implements MetricsGateway {
    other(event: OtherEvent): void {
        console.log(`Other Event: ${event.type}`, event);
    }

    numeric(event: NumericEvent): void {
        console.log(`Numeric Event: ${event.type}`, event);
    }

    error(event: ErrorEvent): void {
        console.error(`Error Event: ${event.type}`, event);
    }
}

class NullMetricsGateway implements MetricsGateway {
    other(event: OtherEvent): void {
        // No-op implementation
    }
    numeric(event: NumericEvent): void {
        // No-op implementation
    }
    error(event: ErrorEvent): void {
        // No-op implementation
    }
}

export class CompositeMetricsGateway implements MetricsGateway {
    constructor(private readonly gateways: MetricsGateway[]) {}

    other(event: OtherEvent): void {
        this.gateways.forEach((provider) => provider.other(event));
    }
    numeric(event: NumericEvent): void {
        this.gateways.forEach((provider) => provider.numeric(event));
    }
    error(event: ErrorEvent): void {
        this.gateways.forEach((provider) => provider.error(event));
    }
}

export class Metrics {
    constructor(
        private readonly providerByEventType: { [eventType: string]: MetricsGateway } = {},
        private readonly defaultProvider: MetricsGateway = new NullMetricsGateway()
    ) {}

    numeric(event: NumericEvent): void {
        const provider = this.providerByEventType[event.type] || this.defaultProvider;
        provider.numeric(event);
    }

    other(event: OtherEvent): void {
        const provider = this.providerByEventType[event.type] || this.defaultProvider;
        provider.other(event);
    }

    error(event: ErrorEvent): void {
        const provider = this.providerByEventType[event.type] || this.defaultProvider;
        provider.error(event);
    }

    async withTimming<T>(
        options: {
            type: string;
            [key: string]: any;
        },
        fn: () => Promise<T>
    ): Promise<T> {
        const start = Date.now();

        try {
            const result = await fn();

            const duration = Date.now() - start;
            this.numeric({
                ...options,
                type: `${options.type}_duration`,
                value: duration,
            });

            return result;
        } catch (error) {
            const duration = Date.now() - start;
            this.error({
                ...options,
                type: `${options.type}_error`,
                value: duration,
                error: error instanceof Error ? error : new Error(String(error)),
            });
            throw error;
        }
    }
}
